import React, { useState, useEffect } from 'react';
import { Table, Pagination, Form, Button, Modal, Row, Col } from 'react-bootstrap';
import ComplexRuleBuilder from './ComplexRuleBuilder'; // Import the new component
import { formatCurrency } from '../../shared/utils/currency'; // Import the utility
import { renderIcon } from '../../shared/utils/iconUtils'; // Import the shared icon utility

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const MONTH_OPTIONS = [
  { value: '01', label: 'January' }, { value: '02', label: 'February' },
  { value: '03', label: 'March' }, { value: '04', label: 'April' },
  { value: '05', label: 'May' }, { value: '06', label: 'June' },
  { value: '07', label: 'July' }, { value: '08', label: 'August' },
  { value: '09', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const ExpenseTable = ({ expenses, categories, onUpdateTransactionCategory, onAddRuleFromTransaction, currency, activeProfileId, onDeleteTransaction, onEditTransaction }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [textFilter, setTextFilter] = useState('');
  const [monthsFilter, setMonthsFilter] = useState([]); // array of '01'..'12'

  // State for categorization modal
  const [showCategorizeModal, setShowCategorizeModal] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [newRule, setNewRule] = useState({
    category: '',
    subcategory: '',
    logical_operator: 'AND',
    conditions: [],
    note: ''
  });

  // State for edit-transaction modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);

  const [paymentSources, setPaymentSources] = useState([]);

  useEffect(() => {
    if (activeProfileId) {
      const token = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).access_token : null;
      
      fetch(`${API_BASE_URL}/api/payment_sources/profiles/${activeProfileId}/payment_sources`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(response => response.ok ? response.json() : [])
        .then(data => setPaymentSources(Array.isArray(data) ? data : []))
        .catch(error => {
          console.error('Error fetching payment sources:', error);
          setPaymentSources([]);
        });
    } else {
      setPaymentSources([]);
    }
  }, [activeProfileId]);

  const filteredExpenses = expenses
    .filter(expense => {
      if (!categoryFilter) return true;
      if (categoryFilter === 'Uncategorized') {
        return expense.category === 'UNCATEGORIZED';
      }
      if (categoryFilter.includes(': ')) { // Filtering by subcategory
        const [mainCategory, subCategory] = categoryFilter.split(': ');
        return expense.category === mainCategory && expense.subcategory === subCategory;
      } else { // Filtering by main category
        return expense.category === categoryFilter;
      }
    })
    .filter(expense => !textFilter || expense.description.toLowerCase().includes(textFilter.toLowerCase()))
    .filter(expense => monthsFilter.length === 0 ||
      monthsFilter.includes((new Date(expense.date).getMonth() + 1).toString().padStart(2, '0')));

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredExpenses.slice(indexOfFirstRow, indexOfLastRow);

  const parsePaymentSourcePayload = (value) => {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0] && typeof parsed[0] === 'object') {
        return parsed[0];
      }
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  };

  const normalizePaymentSource = (value) => {
    if (!value || typeof value !== 'string') return value;
    const parsed = parsePaymentSourcePayload(value);
    if (!parsed) return value;
    return parsed.source_name || parsed.value || parsed.name || parsed.label || value;
  };

  const totalPages = Math.ceil(filteredExpenses.length / rowsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  const handleCategorizeClick = (transaction) => {
    setCurrentTransaction(transaction);
    setNewRule({
      category: '',
      subcategory: '',
      logical_operator: 'AND',
      conditions: [{ field: 'Description', rule_type: 'contains', value: transaction.description }],
      note: ''
    });
    setShowCategorizeModal(true);
  };

  const handleTextFilterChange = (e) => {
    setTextFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleMonthsFilterChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, option => option.value);
    setMonthsFilter(selected);
    setCurrentPage(1);
  };

  const handleDeleteClick = (transactionId) => {
    if (window.confirm("Are you sure you want to delete this transaction?")) {
      onDeleteTransaction(transactionId);
    }
  };

  const handleEditClick = (expense) => {
    setEditingTransaction({
      id: expense.id,
      date: expense.date,
      description: expense.description,
      amount: expense.amount,
      payment_source: expense.payment_source,
      category: expense.category || '',
      subcategory: expense.subcategory || ''
    });
    setShowEditModal(true);
  };

  const handleEditFieldChange = (field, value) => {
    setEditingTransaction(prev => ({
      ...prev,
      [field]: value,
      // Changing category invalidates whatever subcategory was selected.
      ...(field === 'category' ? { subcategory: '' } : {})
    }));
  };

  const handleSaveEdit = () => {
    if (!editingTransaction) return;
    const { date, description, amount, payment_source } = editingTransaction;
    if (!date || !description || amount === '' || amount === null || isNaN(parseFloat(amount)) || !payment_source) {
      alert("Please fill in date, description, amount, and payment source.");
      return;
    }
    onEditTransaction(editingTransaction.id, {
      date,
      description,
      amount: parseFloat(amount),
      payment_source,
      category: editingTransaction.category || null,
      subcategory: editingTransaction.subcategory || null
    });
    setShowEditModal(false);
    setEditingTransaction(null);
  };

  const handleSaveCategorization = () => {

    if (currentTransaction && newRule.category && newRule.conditions.length > 0) {
      onAddRuleFromTransaction(newRule);
      setShowCategorizeModal(false);
    } else {
      alert("Please select a category and add at least one condition for the rule.");
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Form.Group controlId="rowsPerPageSelect">
          <Form.Label className="me-2">Rows per page:</Form.Label>
          <Form.Control as="select" value={rowsPerPage} onChange={handleRowsPerPageChange} style={{ width: '100px', display: 'inline-block' }}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Control>
        </Form.Group>
        <Form.Group controlId="categoryFilter">
          <Form.Label className="me-2">Filter by Category:</Form.Label>
          <Form.Control as="select" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ width: '200px', display: 'inline-block' }}>
            <option value="">All Categories</option>
            <option value="Uncategorized">Uncategorized</option>
            {categories && categories.map((categoryObj, index) => (
              <optgroup key={index} label={categoryObj.name}>
                <option value={categoryObj.name}>{categoryObj.name}</option>
                {categoryObj.subcategories.map((subcategory, subIndex) => {
                  const subcategoryName = typeof subcategory === 'string' ? subcategory : subcategory.name;
                  return (
                    <option key={`${index}-${subIndex}`} value={`${categoryObj.name}: ${subcategoryName}`}>
                      &nbsp;&nbsp;{subcategoryName}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </Form.Control>
        </Form.Group>
        <Pagination>
          <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />
          {[...Array(totalPages)].map((_, index) => (
            <Pagination.Item key={index + 1} active={index + 1 === currentPage} onClick={() => paginate(index + 1)}>
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} />
        </Pagination>
      </div>

      <div className="d-flex align-items-end flex-wrap gap-3 mb-3">
        <Form.Group controlId="textFilter">
          <Form.Label className="me-2">Search Description:</Form.Label>
          <Form.Control
            type="text"
            value={textFilter}
            onChange={handleTextFilterChange}
            placeholder="Search..."
            style={{ width: '220px' }}
          />
        </Form.Group>
        <Form.Group controlId="monthsFilter">
          <Form.Label className="me-2">Month(s):</Form.Label>
          <Form.Control
            as="select"
            multiple
            value={monthsFilter}
            onChange={handleMonthsFilterChange}
            style={{ width: '160px' }}
            title="Ctrl/Cmd-click to select multiple months"
          >
            {MONTH_OPTIONS.map(month => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </Form.Control>
        </Form.Group>
        {(textFilter || monthsFilter.length > 0) && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => { setTextFilter(''); setMonthsFilter([]); setCurrentPage(1); }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      <Table striped bordered hover responsive className="expense-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Payment Source</th>
            <th>Description</th>
            <th>Category</th>
            <th>Subcategory</th>
            <th>Amount</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentRows.map((expense, index) => {
            const parsedPaymentSource = parsePaymentSourcePayload(expense.payment_source);
            const paymentSourceLabel = normalizePaymentSource(expense.payment_source);
            const expenseCategory = categories.find(cat => cat.name === expense.category);
            const expensePaymentSource = Array.isArray(paymentSources) ? 
              paymentSources.find(ps => ps.source_name === paymentSourceLabel) : 
              undefined;
            const paymentSourceIcon = expensePaymentSource?.icon || parsedPaymentSource;
            
            if (index === 0) { // Only log for first row to avoid spam
              if (expensePaymentSource) {
              }
            }

            return (
              <tr key={index}>
                <td>{expense.date}</td>
                <td>
                  <div className="d-flex align-items-center">
                    {renderIcon(paymentSourceIcon, 18, "me-1")}
                    {paymentSourceLabel}
                  </div>
                </td>
                <td>{expense.description}</td>
                <td>
                  <div className="d-flex align-items-center">
                    {renderIcon(expenseCategory?.icon, 18, "me-1")}
                    {expense.category}
                  </div>
                </td>
                <td>{expense.subcategory}</td>
                <td>{formatCurrency(expense.amount, currency)}</td>
                <td>
                  <Button variant="secondary" size="sm" onClick={() => handleEditClick(expense)}>Edit</Button>
                  {expense.category === 'UNCATEGORIZED' && (
                    <Button variant="info" size="sm" className="ms-2" onClick={() => handleCategorizeClick(expense)}>Categorize</Button>
                  )}
                  <Button variant="danger" size="sm" className="ms-2" onClick={() => handleDeleteClick(expense.id)}>Delete</Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Categorization Modal */}
      <Modal show={showCategorizeModal} onHide={() => setShowCategorizeModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Categorize Transaction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentTransaction && (
            <>
              <p><strong>Description:</strong> {currentTransaction.description}</p>
              <p><strong>Amount:</strong> {formatCurrency(currentTransaction.amount, currency)}</p>
              <ComplexRuleBuilder
                rule={newRule}
                onRuleChange={setNewRule}
                categories={categories}
                paymentSources={paymentSources}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCategorizeModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveCategorization}>
            Create Rule
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Transaction Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Transaction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingTransaction && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={editingTransaction.date}
                  onChange={(e) => handleEditFieldChange('date', e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  value={editingTransaction.description}
                  onChange={(e) => handleEditFieldChange('description', e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={editingTransaction.amount}
                  onChange={(e) => handleEditFieldChange('amount', e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Payment Source</Form.Label>
                <Form.Select
                  value={editingTransaction.payment_source}
                  onChange={(e) => handleEditFieldChange('payment_source', e.target.value)}
                >
                  <option value="">Select...</option>
                  {Array.isArray(paymentSources) && paymentSources.map((source, i) => (
                    <option key={i} value={source.source_name}>{source.source_name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={editingTransaction.category}
                  onChange={(e) => handleEditFieldChange('category', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="UNCATEGORIZED">UNCATEGORIZED</option>
                  {categories && categories.map((categoryObj, index) => (
                    <option key={index} value={categoryObj.name}>{categoryObj.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Subcategory</Form.Label>
                <Form.Select
                  value={editingTransaction.subcategory}
                  onChange={(e) => handleEditFieldChange('subcategory', e.target.value)}
                  disabled={!editingTransaction.category}
                >
                  <option value="">Select...</option>
                  {editingTransaction.category &&
                    categories.find(c => c.name === editingTransaction.category)?.subcategories.map((subcategory, index) => {
                      const subcategoryName = typeof subcategory === 'string' ? subcategory : subcategory.name;
                      return (
                        <option key={index} value={subcategoryName}>{subcategoryName}</option>
                      );
                    })}
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ExpenseTable;
